import React, { useState } from 'react';
import { PlayIcon, VideoIcon, Volume2Icon, MaximizeIcon } from 'lucide-react';
interface VideoReplayProps {
  /** URL de Vimeo, Wistia, YouTube o archivo propio. */
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  title?: string;
  /** Texto superpuesto sobre la miniatura, editable desde el constructor. */
  overlayTitle?: string;
  className?: string;
  /** Ancho máximo del reproductor. */
  maxWidthClass?: string;
}
function toEmbedUrl(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}
function isFileUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Reproductor de vídeo reutilizable en proporción 16:9, protagonista del hero.
 * Se usa en la landing de AVA y en cualquier landing con bloque de vídeo.
 */
export function VideoReplay({
  url,
  thumbnailUrl,
  caption,
  title = 'Vídeo replay',
  overlayTitle,
  className = '',
  maxWidthClass = 'max-w-5xl'
}: VideoReplayProps) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = url ? toEmbedUrl(url) : null;
  const hasSource = Boolean(url);
  return <figure className={`w-full ${maxWidthClass} mx-auto ${className}`} data-id="element-553" data-mp="2mp8rt0echiwm">
      <div className="ava-video-shell relative w-full overflow-hidden rounded-2xl bg-navy ring-1 ring-black/5 sm:rounded-3xl" data-id="element-554" data-mp="2rnabnnjlhm95">
        <div className="relative aspect-video w-full" data-id="element-555" data-mp="2vq6zbsnzs8jy">
          {playing && hasSource ? embedUrl ? <iframe src={embedUrl} title={title} className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen data-id="element-556" data-mp="30nozatk17dft" /> : isFileUrl(url as string) ? <video src={url} controls autoPlay className="absolute inset-0 h-full w-full object-cover" data-id="element-557" data-mp="24m0bw9iygank" /> : <iframe src={url} title={title} className="absolute inset-0 h-full w-full" allowFullScreen data-id="element-558" data-mp="2dmos9avtmn3m" /> : <button type="button" onClick={() => hasSource && setPlaying(true)} aria-label={hasSource ? 'Reproducir el vídeo' : 'Vídeo pendiente de configurar'} className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40" data-id="element-559" data-mp="2nl4m3lm8gagt">
              {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" data-id="element-560" data-mp="36c5hy0s23pw5" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface text-muted-foreground" data-id="element-561" data-mp="3uvz0vuorrkfn">
                  <VideoIcon className="h-8 w-8" aria-hidden="true" data-id="element-562" data-mp="3fxyd5ny1oewl" />
                  <span className="text-sm" data-id="element-563" data-mp="3kxt4fslqf6i6">Miniatura pendiente</span>
                </span>}

              <span className="absolute inset-0 bg-gradient-to-t from-black-premium/35 via-transparent to-transparent" data-id="element-564" data-mp="3764pzio0g5y1" />

              {overlayTitle && <span className="absolute left-5 top-5 max-w-[80%] text-left text-base font-semibold leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.35)] sm:left-8 sm:top-8 sm:text-xl" data-id="element-565" data-mp="3bzhfdv0ikgru">
                  {overlayTitle}
                </span>}

              {/* Botón de reproducción */}
              <span className="absolute left-1/2 top-1/2 flex h-[72px] w-[72px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out group-hover:scale-105 sm:h-24 sm:w-24" data-id="element-566" data-mp="2s9e0c14528lm">
                <span className="absolute inset-0 rounded-full ring-8 ring-white/25" data-id="element-567" data-mp="2g9gsvzj75mfp" />
                <PlayIcon className="ml-1 h-7 w-7 text-primary sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true" data-id="element-568" data-mp="2beb50v68hxoz" />
              </span>

              {/* Barra de controles decorativa */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3 text-white/90 sm:px-6 sm:py-4" data-id="element-569" data-mp="1xfl7jefi5w03">
                <PlayIcon className="h-4 w-4 flex-none" fill="currentColor" aria-hidden="true" data-id="element-570" data-mp="1shs3aqxrfc2g" />
                <span className="text-[11px] tabular-nums sm:text-xs" data-id="element-571" data-mp="1nk9tyzftyutx">0:00 / 4:37</span>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-572" data-mp="3mv15dxnz7vao">
                  <span className="block h-full w-[4%] rounded-full bg-white" data-id="element-573" data-mp="3hxrk8u7me5sn" />
                </span>
                <Volume2Icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-574" data-mp="3qcp66hqze1vc" />
                <MaximizeIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-575" data-mp="3ueadzoonrpyo" />
              </span>

              {!hasSource && <span className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-muted-foreground" data-id="element-576" data-mp="316cmu9fznlp">
                  Añade la URL del vídeo desde el editor
                </span>}
            </button>}
        </div>
      </div>

      {caption && <figcaption className="mx-auto mt-8 max-w-2xl text-center text-base text-muted-foreground sm:text-lg" data-id="element-577" data-mp="3bi13fbcythjf">
          {caption}
        </figcaption>}
    </figure>;
}